import React from 'react';
import {
   Button,
   Dialog,
   DialogActions,
   DialogContent,
   IconButton,
   Stack,
   Typography,
} from '@mui/material';
import { Login as LoginIcon, PersonAdd as PersonAddIcon, Close as CloseIcon } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';

export interface WetrippenAuthPromoDialogProps {
   open: boolean;
   onClose: () => void;
   onLogin: () => void;
   onGetStarted: () => void;
   /** Optional contextual line (e.g. "Sign in to leave a comment"). */
   context?: string;
}

/**
 * Modal variant of the shared-collection Wetrippen promo — shown when a guest
 * attempts an action that requires an account (e.g. commenting).
 */
const WetrippenAuthPromoDialog: React.FC<WetrippenAuthPromoDialogProps> = ({
   open,
   onClose,
   onLogin,
   onGetStarted,
   context,
}) => {
   const theme = useTheme();

   const handleLogin = () => {
      onClose();
      onLogin();
   };

   const handleGetStarted = () => {
      onClose();
      onGetStarted();
   };

   return (
      <Dialog
         open={open}
         onClose={onClose}
         maxWidth='xs'
         fullWidth
         aria-labelledby='wetrippen-auth-promo-title'
         BackdropProps={{
            sx: {
               bgcolor: alpha(theme.palette.common.black, 0.62),
               backdropFilter: 'blur(2px)',
            },
         }}
         PaperProps={{
            sx: {
               borderRadius: 2,
               border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
               overflow: 'hidden',
               bgcolor: alpha(theme.palette.background.paper, 0.98),
               backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(
                  theme.palette.primary.dark,
                  0.12,
               )} 100%)`,
               boxShadow: `0 20px 50px ${alpha(theme.palette.common.black, 0.45)}`,
            },
         }}
      >
         <DialogContent sx={{ pt: 2.5, pb: 1, px: 2.5, position: 'relative' }}>
            <IconButton
               aria-label='Close'
               onClick={onClose}
               size='small'
               sx={{ position: 'absolute', right: 8, top: 8 }}
            >
               <CloseIcon />
            </IconButton>
            {context ? (
               <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', mb: 1, pr: 5, fontWeight: 600 }}
               >
                  {context}
               </Typography>
            ) : null}
            <Typography
               id='wetrippen-auth-promo-title'
               variant='subtitle1'
               fontWeight={700}
               component='p'
               sx={{ mb: 0.5, pr: 5 }}
            >
               Create beautiful collections with Wetrippen
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
               Share trips and albums in one place.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap='wrap'>
               <Button
                  variant='outlined'
                  color='inherit'
                  size='medium'
                  startIcon={<LoginIcon />}
                  onClick={handleLogin}
                  sx={{ textTransform: 'none' }}
               >
                  Log in
               </Button>
               <Button
                  variant='contained'
                  color='primary'
                  size='medium'
                  disableElevation
                  startIcon={<PersonAddIcon />}
                  onClick={handleGetStarted}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
               >
                  Get started
               </Button>
            </Stack>
         </DialogContent>
         <DialogActions sx={{ px: 2.5, pb: 2, pt: 0, justifyContent: 'flex-start' }}>
            <Button color='inherit' onClick={onClose} sx={{ textTransform: 'none' }}>
               Cancel
            </Button>
         </DialogActions>
      </Dialog>
   );
};

export default WetrippenAuthPromoDialog;
